import React, { useState, useEffect } from 'react';
import { ShoppingCart, Globe, Shield, Lock, Mail, Key, ArrowRight, Bot, Sparkles, RefreshCw, AlertCircle, CheckCircle, Building2, User, UserCheck } from 'lucide-react';

interface SystemLoginModalProps {
  onClose?: () => void;
  onLoginSuccess: (type: 'pos' | 'tenet' | 'godmode' | 'corporate_admin' | 'corporate_user', tokenOrData: any) => void;
  initialTab?: 'pos' | 'tenet' | 'corporate' | 'tenet_register';
}

export default function SystemLoginModal({ onClose, onLoginSuccess, initialTab }: SystemLoginModalProps) {
  const [activeTab, setActiveTab] = useState<'pos' | 'tenet' | 'corporate'>(
    initialTab === 'tenet_register' ? 'tenet' : (initialTab || 'pos')
  );
  const [tenetMode, setTenetMode] = useState<'login' | 'register'>(
    initialTab === 'tenet_register' ? 'register' : 'login'
  );

  useEffect(() => {
    if (initialTab === 'tenet_register') {
      setActiveTab('tenet');
      setTenetMode('register');
    } else if (initialTab) {
      setActiveTab(initialTab);
      setTenetMode('login');
    }
  }, [initialTab]);

  // Tenet Login State
  const [tenetEmail, setTenetEmail] = useState<string>('');
  const [tenetPassword, setTenetPassword] = useState<string>('');

  // Tenet Registration State
  const [regBusinessName, setRegBusinessName] = useState<string>('');
  const [regEmail, setRegEmail] = useState<string>('');
  const [regPassword, setRegPassword] = useState<string>('');
  const [regPhone, setRegPhone] = useState<string>('');
  const [regWebsite, setRegWebsite] = useState<string>('');

  // Corporate Suite Login State
  const [corpSubTab, setCorpSubTab] = useState<'client' | 'admin'>('client');
  const [corpEmail, setCorpEmail] = useState<string>('');
  const [corpPassword, setCorpPassword] = useState<string>('');

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleTenetLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/tenet/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: tenetEmail, password: tenetPassword })
      });
      const data = await res.json();

      if (data.success) {
        localStorage.setItem('tenet_client_token', data.token);
        localStorage.setItem('tenet_auth_token', data.token);
        onLoginSuccess('tenet', data);
      } else {
        setError(data.error || 'Login failed. Check your credentials.');
      }
    } catch (err: any) {
      setError(err?.message || 'Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  // 🚫 v6.13.71: self-serve registration temporarily closed — hide the form entirely.
  const [regClosed, setRegClosed] = useState(false);
  useEffect(() => {
    fetch('/api/tenet/auth/register', { cache: 'no-store' })
      .then((r) => r.json())
      .then((j) => { if (j?.registrationOpen === false) setRegClosed(true); })
      .catch(() => {});
  }, []);

  const handleTenetRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/tenet/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: regBusinessName,
          email: regEmail,
          password: regPassword,
          phone: regPhone,
          websiteUrl: regWebsite
        })
      });
      const data = await res.json();

      if (data.success && data.token) {
        localStorage.setItem('tenet_client_token', data.token);
        localStorage.setItem('tenet_auth_token', data.token);
        onLoginSuccess('tenet', data);
      } else {
        setError(data.error || 'Registration failed. Check your information.');
      }
    } catch (err: any) {
      setError(err?.message || 'Network error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  const handleCorporateLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const endpoint = corpSubTab === 'admin'
        ? '/api/corporate/admin/login'
        : '/api/corporate/user/login';

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: corpEmail, password: corpPassword })
      });
      const data = await res.json();

      if (data.success) {
        if (corpSubTab === 'admin') {
          localStorage.setItem('corporate_admin_token', data.token);
          onLoginSuccess('corporate_admin', data);
        } else {
          localStorage.setItem('corporate_user_token', data.token);
          onLoginSuccess('corporate_user', data);
        }
      } else {
        setError(data.error || 'Login failed. Check your corporate credentials.');
      }
    } catch (err: any) {
      setError(err?.message || 'Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl relative overflow-hidden">
        {/* Top Glow Accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-cyan-500 to-blue-500" />

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center mx-auto mb-3 shadow-lg shadow-blue-500/20">
            <Bot className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-xl font-black text-white tracking-wide">SYSTEM LOGIN PORTAL</h2>
          <p className="text-xs text-slate-400 mt-1">Select your ecosystem account type to proceed</p>
        </div>

        {/* Destination Tabs (3-way) */}
        <div className="grid grid-cols-3 gap-1.5 bg-slate-950 p-1.5 rounded-2xl mb-6 border border-slate-800">
          <button
            type="button"
            onClick={() => { setActiveTab('pos'); setError(null); }}
            className={`py-2 px-2 rounded-xl font-bold text-[11px] flex items-center justify-center gap-1.5 transition ${
              activeTab === 'pos' 
                ? 'bg-emerald-500 text-slate-950 shadow-md font-black' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <ShoppingCart className="w-3.5 h-3.5" />
            <span>POS</span>
          </button>

          <button
            type="button"
            onClick={() => { setActiveTab('tenet'); setError(null); }}
            className={`py-2 px-2 rounded-xl font-bold text-[11px] flex items-center justify-center gap-1.5 transition ${
              activeTab === 'tenet' 
                ? 'bg-cyan-500 text-slate-950 shadow-md font-black' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Globe className="w-3.5 h-3.5" />
            <span>Tenet</span>
          </button>

          <button
            type="button"
            onClick={() => { setActiveTab('corporate'); setError(null); }}
            className={`py-2 px-2 rounded-xl font-bold text-[11px] flex items-center justify-center gap-1.5 transition ${
              activeTab === 'corporate' 
                ? 'bg-blue-500 text-slate-950 shadow-md font-black' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Building2 className="w-3.5 h-3.5" />
            <span>Corporate</span>
          </button>
        </div>

        {error && (
          <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs px-4 py-3 rounded-xl mb-4 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Tab Content 1: POS System Login */}
        {activeTab === 'pos' && (
          <div className="space-y-4 text-center">
            <p className="text-xs text-slate-400">Launch KangaVena POS Store Cashier & Manager Terminal</p>
            <button
              type="button"
              onClick={() => onLoginSuccess('pos', null)}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-sm py-3.5 rounded-xl shadow-lg shadow-emerald-500/20 transition flex items-center justify-center gap-2"
            >
              <ShoppingCart className="w-4 h-4" />
              <span>Launch POS Cashier Terminal</span>
            </button>
          </div>
        )}

        {/* Tab Content 2: Tenet Dynamic AI Workspace (Login & Register) */}
        {activeTab === 'tenet' && (
          <div className="space-y-4">
            {/* Tenet Mode Toggle (Sign In vs Register 100k Free Trial) */}
            <div className="flex items-center p-1 bg-slate-950 rounded-xl border border-slate-800 text-[11px] font-bold">
              <button
                type="button"
                onClick={() => { setTenetMode('login'); setError(null); }}
                className={`flex-1 py-1.5 rounded-lg transition text-center cursor-pointer ${
                  tenetMode === 'login'
                    ? 'bg-cyan-500 text-slate-950 font-black shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => { setTenetMode('register'); setError(null); }}
                className={`flex-1 py-1.5 rounded-lg transition text-center cursor-pointer flex items-center justify-center gap-1 ${
                  tenetMode === 'register'
                    ? 'bg-gradient-to-r from-cyan-500 to-emerald-500 text-slate-950 font-black shadow-sm'
                    : 'text-emerald-400 hover:text-emerald-300'
                }`}
              >
                <Sparkles size={11} />
                <span>Free Trial (100k Tokens)</span>
              </button>
            </div>

            {tenetMode === 'login' ? (
              <form onSubmit={handleTenetLogin} className="space-y-3.5">
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Tenet Client Email</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-500 absolute left-3.5 top-3" />
                    <input
                      type="email"
                      required
                      value={tenetEmail}
                      onChange={(e) => setTenetEmail(e.target.value)}
                      placeholder="client@company.com"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-2 text-xs text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Password</label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-slate-500 absolute left-3.5 top-3" />
                    <input
                      type="password"
                      required
                      value={tenetPassword}
                      onChange={(e) => setTenetPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-2 text-xs text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-black text-xs uppercase tracking-wider py-2.5 rounded-xl shadow-lg shadow-cyan-500/20 transition flex items-center justify-center gap-2 mt-3 cursor-pointer"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  <span>Log Into Tenet Portal</span>
                </button>
              </form>
            ) : regClosed ? (
              <div className="p-4 rounded-xl bg-slate-950 border border-amber-500/30 text-[12px] text-amber-300 leading-relaxed">
                <strong className="block mb-1 text-amber-200">🚫 Registration temporarily closed</strong>
                New self-serve signups are paused right now. Please contact the team to create your account — we will get you set up instantly.
                <span className="block mt-2 text-slate-400">නව ලියාපදිංචිය තාවකාලිකව අත්හිටුවා ඇත. ගිණුමක් සෑදීමට කණ්ඩායම අමතන්න.</span>
              </div>
            ) : (
              <form onSubmit={handleTenetRegister} className="space-y-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-r from-cyan-950/40 via-emerald-950/40 to-slate-900 border border-emerald-500/30 text-[11px] text-emerald-300 flex items-center gap-2">
                  <Sparkles size={16} className="text-amber-300 shrink-0" />
                  <span><strong>100,000 Free Agent Tokens</strong> granted instantly. Test full flows & features.</span>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-300 mb-1">Business / Agency Name *</label>
                  <input
                    type="text"
                    required
                    value={regBusinessName}
                    onChange={(e) => setRegBusinessName(e.target.value)}
                    placeholder="e.g. Nexus Corp / Acme AI"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-300 mb-1">Admin Email *</label>
                    <input
                      type="email"
                      required
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      placeholder="admin@nexus.com"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-300 mb-1">Password *</label>
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-300 mb-1">WhatsApp / Phone</label>
                    <input
                      type="text"
                      value={regPhone}
                      onChange={(e) => setRegPhone(e.target.value)}
                      placeholder="07x xxx xxxx"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-300 mb-1">Website URL</label>
                    <input
                      type="text"
                      value={regWebsite}
                      onChange={(e) => setRegWebsite(e.target.value)}
                      placeholder="sample.com"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-slate-950 font-black text-xs uppercase tracking-wider py-2.5 rounded-xl shadow-lg shadow-emerald-500/20 transition flex items-center justify-center gap-2 mt-3 cursor-pointer"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  <span>Claim 100k Tokens & Launch</span>
                </button>
              </form>
            )}
          </div>
        )}

        {/* Tab Content 3: Corporate Suite Login */}
        {activeTab === 'corporate' && (
          <form onSubmit={handleCorporateLogin} className="space-y-4">
            {/* Sub-selector for Client User vs Firm Admin */}
            <div className="grid grid-cols-2 gap-2 bg-slate-950 p-1 rounded-xl border border-slate-800 mb-3">
              <button
                type="button"
                onClick={() => { setCorpSubTab('client'); setError(null); }}
                className={`py-1.5 rounded-lg text-xs font-bold transition ${
                  corpSubTab === 'client'
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Client User
              </button>
              <button
                type="button"
                onClick={() => { setCorpSubTab('admin'); setError(null); }}
                className={`py-1.5 rounded-lg text-xs font-bold transition ${
                  corpSubTab === 'admin'
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Firm Admin
              </button>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1.5">
                {corpSubTab === 'admin' ? 'Firm Admin Email' : 'Client Account Email'}
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-500 absolute left-3.5 top-3.5" />
                <input
                  type="email"
                  required
                  value={corpEmail}
                  onChange={(e) => setCorpEmail(e.target.value)}
                  placeholder={corpSubTab === 'admin' ? 'admin@advisoryfirm.com' : 'user@company.com'}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-2.5 text-xs text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-500 absolute left-3.5 top-3.5" />
                <input
                  type="password"
                  required
                  value={corpPassword}
                  onChange={(e) => setCorpPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-2.5 text-xs text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 text-slate-950 font-black text-xs uppercase tracking-wider py-3 rounded-xl shadow-lg shadow-blue-500/25 transition flex items-center justify-center gap-2 mt-4"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              <span>
                {corpSubTab === 'admin' ? 'Log Into Firm Admin Dashboard' : 'Log Into Client Filing Portal'}
              </span>
            </button>
          </form>
        )}

        {onClose && (
          <button
            onClick={onClose}
            className="w-full mt-4 text-xs font-bold text-slate-500 hover:text-slate-300 transition py-2"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
